// ============================================================
// NOVATEK INDUSTRIAL SYSTEMS - REUSABLE MEDIA PICKER MODAL
// ============================================================

window.MediaPickerModal = function MediaPickerModal({
  isOpen,
  onClose,
  onSelect,
  title = "Choose from Media Library",
  filterType = "images"
}) {
  const [mediaList, setMediaList] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [sourceFilter, setSourceFilter] = React.useState('All');
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const uploadInputRef = React.useRef(null);

  React.useEffect(() => {
    if (isOpen) {
      fetchMedia();
      setSelectedItem(null);
      setSearchQuery('');
    }
  }, [isOpen]);

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const res = await window.apiClient.get('media.php');
      if (res.success && Array.isArray(res.data)) {
        setMediaList(res.data);
      } else {
        setMediaList([]);
      }
    } catch (e) {
      console.warn('Failed to load media library:', e);
      setMediaList([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadNew = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await window.apiClient.uploadFile('media.php', formData);
      if (res.success && res.url) {
        const newItem = {
          id: res.id || Date.now(),
          file_path: res.url,
          original_name: file.name,
          filename: res.filename || file.name,
          file_type: file.type,
          file_size: file.size,
          source: 'Uploaded'
        };
        setMediaList(prev => [newItem, ...prev]);
        setSelectedItem(newItem);
      }
    } catch (err) {
      alert(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (uploadInputRef.current) uploadInputRef.current.value = '';
    }
  };

  const handleSelectConfirm = () => {
    if (!selectedItem) return;
    onSelect(selectedItem.file_path, selectedItem);
    onClose();
  };

  if (!isOpen) return null;

  // Filter media
  const filteredList = mediaList.filter(item => {
    const ft = item.file_type || '';
    const fp = item.file_path || '';
    const isImg = ft.startsWith('image/') || fp.match(/\.(png|jpg|jpeg|webp|svg|gif)$/i);
    
    if (filterType === 'images' && !isImg) return false;
    
    const matchesSource = sourceFilter === 'All' || (item.source || 'Uploaded') === sourceFilter;
    const nameMatch = (item.original_name || item.filename || fp).toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSource && nameMatch;
  });

  const sources = ['All', ...Array.from(new Set(mediaList.map(m => m.source || 'Uploaded')))];

  return (
    <div className="fixed inset-0 z-[100] bg-slate-950/75 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-4xl w-full h-[85vh] flex flex-col shadow-2xl overflow-hidden animate-fadeIn border border-slate-200">
        
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-orange-100 text-secondary flex items-center justify-center">
              <span className="material-symbols-outlined text-xl">photo_library</span>
            </div>
            <div>
              <h3 className="font-sans text-base font-bold text-slate-900">{title}</h3>
              <p className="font-sans text-xs text-slate-500">Select an existing image from storage or upload a new one directly.</p>
            </div>
          </div>

          <button 
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 rounded-full transition-colors"
          >
            <span className="material-symbols-outlined text-xl">close</span>
          </button>
        </div>

        {/* Toolbar (Search, Filter, Upload) */}
        <div className="px-6 py-3 border-b border-slate-100 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-white shrink-0">
          <div className="flex items-center gap-2 flex-1 max-w-md">
            <div className="relative flex-1">
              <span className="material-symbols-outlined text-slate-400 absolute left-3 top-2.5 text-base">search</span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search images by name..."
                className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none focus:border-secondary font-sans"
              />
            </div>

            {sources.length > 2 && (
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-sans outline-none focus:border-secondary"
              >
                {sources.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <input 
              type="file" 
              ref={uploadInputRef} 
              onChange={handleUploadNew} 
              accept="image/*" 
              className="hidden" 
            />
            <button
              onClick={() => uploadInputRef.current && uploadInputRef.current.click()}
              disabled={uploading}
              className="bg-slate-900 hover:bg-secondary text-white font-sans text-xs font-bold px-4 py-2 rounded-xl transition-all shadow flex items-center gap-1.5 active:scale-95 disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-base">{uploading ? 'sync' : 'upload_file'}</span>
              <span>{uploading ? 'Uploading...' : 'Upload New Image'}</span>
            </button>
          </div>
        </div>

        {/* Gallery Grid */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50 custom-scrollbar">
          {loading ? (
            <div className="h-full flex flex-col items-center justify-center text-slate-400 text-xs font-sans gap-2">
              <span className="material-symbols-outlined text-3xl animate-spin text-secondary">progress_activity</span>
              <span>Loading media assets...</span>
            </div>
          ) : filteredList.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center p-8">
              <div className="h-16 w-16 bg-slate-100 rounded-2xl flex items-center justify-center text-slate-400 mb-3">
                <span className="material-symbols-outlined text-3xl">image_not_supported</span>
              </div>
              <h4 className="font-sans text-sm font-bold text-slate-700">No media assets found</h4>
              <p className="font-sans text-xs text-slate-500 max-w-xs mt-1">
                Upload images from your computer using the "Upload New Image" button above.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {filteredList.map((item, idx) => {
                const isSelected = selectedItem?.file_path === item.file_path;
                const mediaUrl = window.resolveMediaUrl(item.file_path, 'machine');
                return (
                  <div
                    key={item.id || idx}
                    onClick={() => setSelectedItem(item)}
                    onDoubleClick={() => {
                      onSelect(item.file_path, item);
                      onClose();
                    }}
                    className={`group relative bg-white rounded-2xl border-2 overflow-hidden cursor-pointer transition-all flex flex-col justify-between ${
                      isSelected 
                        ? 'border-secondary shadow-lg ring-2 ring-orange-400/30' 
                        : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
                    }`}
                  >
                    <div className="h-28 w-full bg-slate-900/5 relative overflow-hidden flex items-center justify-center p-2">
                      <img
                        src={mediaUrl}
                        alt={item.original_name || item.filename}
                        onError={(e) => window.handleImageError(e, 'machine')}
                        className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform"
                        loading="lazy"
                      />
                      {isSelected && (
                        <div className="absolute top-2 right-2 bg-secondary text-white rounded-full p-0.5 shadow">
                          <span className="material-symbols-outlined text-sm block">check</span>
                        </div>
                      )}
                    </div>

                    <div className="p-2.5 bg-white border-t border-slate-100">
                      <p className="font-sans text-[11px] font-bold text-slate-800 truncate" title={item.original_name || item.filename}>
                        {item.original_name || item.filename}
                      </p>
                      <div className="flex justify-between items-center text-[10px] text-slate-500 mt-1">
                        <span className="truncate">{item.source || 'Storage'}</span>
                        {item.file_size && <span>{Math.round(item.file_size / 1024)} KB</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-slate-200 bg-white flex flex-col sm:flex-row justify-between items-center gap-3 shrink-0">
          <div className="text-xs font-sans text-slate-500 truncate w-full sm:w-auto">
            {selectedItem ? (
              <span className="text-slate-800 font-medium">
                Selected: <strong>{selectedItem.original_name || selectedItem.filename}</strong> ({selectedItem.file_path})
              </span>
            ) : (
              <span>Click an image to select it, or double-click to insert immediately.</span>
            )}
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-xs font-sans font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSelectConfirm}
              disabled={!selectedItem}
              className="px-6 py-2.5 rounded-xl bg-secondary hover:bg-orange-600 disabled:opacity-40 text-white text-xs font-sans font-bold shadow-md transition-all flex items-center gap-1.5"
            >
              <span className="material-symbols-outlined text-sm">check_circle</span>
              <span>Insert Selected Image</span>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};
